import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";

// Build-time diagnostic — visible in browser console on every page load.
// Look for "[SourceDeck Analytics]" in DevTools console to confirm build baked the IDs.
if (typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.log(
    "[SourceDeck Analytics] Build v2 · GA4:",
    GA4_ID ? `configured (${GA4_ID.slice(0, 4)}...)` : "NOT SET at build time",
    "· Meta Pixel:",
    META_PIXEL_ID ? `configured (${META_PIXEL_ID.slice(0, 4)}...)` : "NOT SET at build time",
  );
}

let ga4Loaded = false;
let metaLoaded = false;

function loadGA4() {
  if (ga4Loaded || !GA4_ID) return;
  ga4Loaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, { send_page_view: false });
}

function loadMetaPixel() {
  if (metaLoaded || !META_PIXEL_ID) return;
  metaLoaded = true;
  // Standard Meta Pixel snippet, minified
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", META_PIXEL_ID);
}

// Load scripts immediately at module import — don't depend on React mount timing.
// This runs during initial JS bundle parse in the browser.
if (typeof window !== "undefined") {
  loadGA4();
  loadMetaPixel();
}

/** Track a custom conversion event across both GA4 + Meta Pixel. */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  try {
    if (window.gtag && GA4_ID) window.gtag("event", name, params);
    if (window.fbq && META_PIXEL_ID) window.fbq("trackCustom", name, params);
  } catch (e) { /* fail silent — never break UX for analytics */ }
}

export default function Analytics() {
  const location = useLocation();
  const firstLoad = useRef(true);

  useEffect(() => {
    // Scripts already loaded at module import; safety re-check.
    loadGA4();
    loadMetaPixel();
  }, []);

  useEffect(() => {
    const path = location.pathname + location.search;
    if (window.gtag && GA4_ID) {
      window.gtag("event", "page_view", {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    if (window.fbq && META_PIXEL_ID) {
      window.fbq("track", firstLoad.current ? "PageView" : "PageView", { path });
    }
    firstLoad.current = false;
  }, [location.pathname, location.search]);

  return null;
}
